import { InfoCircleOutlined, MailOutlined, PhoneOutlined, PlusOutlined } from '@ant-design/icons';
import { CloseOutlined } from '@mui/icons-material';
import { Select, Tabs, Flex, Form, Input, Button, Checkbox, Switch, Tooltip } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { TeamMemberRole, TeamMemberRoleOptions } from 'interfaces/teams';
import { ChangeEvent, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import type { ShareRecipientContactType } from 'utilities/share-recipient-validation';
import { getShareRecipientAutoComplete, getShareRecipientError } from 'utilities/share-recipient-validation';
import { UserSelect } from '@sitemax/smx-components';
import { setterShareable } from '../../slices/shareablesSlice';
import type { ShareRecipient } from '../../slices/shareablesSlice';

export type SharedTabType = 'members' | 'invitations';

export const SelectSharingDocumentTabs = ({
    isLoaded,
    setCurrentTab,
    role,
    setRole,
}: {
    isLoaded: boolean;
    setCurrentTab: (value: SharedTabType) => void;
    role: TeamMemberRole;
    setRole: (value: TeamMemberRole) => void;
}) => {
    const dispatch = useAppDispatch();
    const { shareRecipients, includeAnnotations, shareRecipientsError } = useAppSelector((state) => state.shareables);
    const { isDevelopmentMode } = useAppSelector((state) => state.development);
    const isNewSharingMode = isDevelopmentMode;
    const showRemoveButton = shareRecipients.length > 1;
    const loaded = useRef(false);
    const dismissTimerRef = useRef<number | null>(null);

    useEffect(() => {
        loaded.current = isLoaded;
    }, [isLoaded]);

    useEffect(() => {
        if (!shareRecipientsError) {
            if (dismissTimerRef.current) {
                window.clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = null;
            }
            return;
        }

        if (dismissTimerRef.current) {
            window.clearTimeout(dismissTimerRef.current);
        }

        dismissTimerRef.current = window.setTimeout(() => {
            dispatch(setterShareable({ shareRecipientsError: '' }));
        }, 3000);

        return () => {
            if (dismissTimerRef.current) {
                window.clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = null;
            }
        };
    }, [dispatch, shareRecipientsError]);

    const handleRoleChange = (value: string) => {
        setRole(value as TeamMemberRole);
    };

    const clearShareRecipientsError = () => {
        if (!shareRecipientsError) return;
        dispatch(setterShareable({ shareRecipientsError: '' }));
    };

    const roleSelect = (
        <Select
            defaultValue={role}
            onChange={handleRoleChange}
            popupClassName="ant-picker-dropdown"
            style={{ marginLeft: 10 }}
        >
            {TeamMemberRoleOptions.map((option: any) => {
                return (
                    <Select.Option key={option.value} value={option.value}>
                        {option.label}
                    </Select.Option>
                );
            })}
        </Select>
    );

    const items = [
        {
            key: 'members',
            label: (
                <Tooltip
                    title="Setting a visibility group on a document or category allows only the specified visibility group members or document managers to see the file."
                    overlayInnerStyle={{ padding: '12px 16px' }}
                >
                    <Flex align="center" gap={6}>
                        Manage Access
                        <InfoCircleOutlined />
                    </Flex>
                </Tooltip>
            ),
            children: (
                <Flex style={{ padding: '10px 0' }}>
                    <Flex flex={1}>
                        <UserSelect
                            mode={'multiple'}
                            style={{ width: '100%' }}
                            onChange={(value: any) => {
                                dispatch(setterShareable({ memberIdListSelected: value }));
                            }}
                        />
                    </Flex>

                    {roleSelect}
                </Flex>
            ),
        },
        {
            key: 'invitations',
            label: 'Share by Link',
            children: (
                <div style={{ position: 'relative' }}>
                    <div
                        aria-live="polite"
                        role="status"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            display: 'flex',
                            justifyContent: 'center',
                            pointerEvents: 'none',
                            zIndex: 1,
                        }}
                    >
                        <div
                            style={{
                                maxWidth: 520,
                                padding: '4px 10px',
                                borderRadius: 8,
                                fontSize: 12,
                                lineHeight: 1.3,
                                backgroundColor: '#fff7ed',
                                border: '1px solid #fdba74',
                                color: '#9a3412',
                                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
                                opacity: shareRecipientsError ? 1 : 0,
                                transform: shareRecipientsError ? 'translateY(0)' : 'translateY(-6px)',
                                transition: 'opacity 180ms ease, transform 180ms ease',
                            }}
                        >
                            {shareRecipientsError}
                        </div>
                    </div>
                    {shareRecipients.map((recipient: ShareRecipient, index: number) => {
                        const contactType = recipient.contactType ?? 'email';
                        const error = getShareRecipientError(
                            recipient.contact ?? '',
                            isNewSharingMode ? contactType : undefined,
                        );
                        const inputPlaceholder = isNewSharingMode
                            ? contactType === 'phone'
                                ? 'Phone number'
                                : 'Email address'
                            : 'Send to email or phone number';
                        const inputType = isNewSharingMode ? (contactType === 'phone' ? 'tel' : 'email') : 'text';
                        const inputMode = isNewSharingMode ? (contactType === 'phone' ? 'tel' : 'email') : undefined;
                        const autoComplete = isNewSharingMode
                            ? getShareRecipientAutoComplete(index, contactType)
                            : undefined;
                        const gridTemplateColumns = isNewSharingMode
                            ? showRemoveButton
                                ? '72px 1fr 32px'
                                : '72px 1fr'
                            : showRemoveButton
                              ? '1fr 32px'
                              : '1fr';
                        return (
                            <div
                                key={index}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: gridTemplateColumns,
                                    columnGap: 16,
                                    rowGap: 4,
                                    alignItems: 'center',
                                    padding: '10px 0',
                                }}
                            >
                                {isNewSharingMode && (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <Switch
                                            checked={contactType === 'phone'}
                                            onChange={(checked: boolean) => {
                                                clearShareRecipientsError();
                                                const nextType: ShareRecipientContactType = checked ? 'phone' : 'email';
                                                const _recipient = { ...recipient, contactType: nextType };
                                                const _shareRecipients = [...shareRecipients];
                                                _shareRecipients[index] = _recipient;
                                                dispatch(setterShareable({ shareRecipients: _shareRecipients }));
                                            }}
                                            checkedChildren={<PhoneOutlined />}
                                            unCheckedChildren={<MailOutlined />}
                                            style={{
                                                transform: 'scale(1.4)',
                                                transformOrigin: 'center',
                                                ...(contactType === 'email'
                                                    ? { backgroundColor: '#f59e0b', borderColor: '#f59e0b' }
                                                    : {}),
                                            }}
                                        />
                                    </div>
                                )}
                                <Form.Item style={{ marginBottom: 0 }} validateStatus={error ? 'error' : undefined}>
                                    <Input
                                        id={`share-recipient-${contactType}-${index}`}
                                        name={`share-recipient-${contactType}-${index}`}
                                        placeholder={inputPlaceholder}
                                        type={inputType}
                                        inputMode={inputMode}
                                        autoComplete={autoComplete}
                                        value={recipient.contact}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            clearShareRecipientsError();
                                            const _recipient = {
                                                ...recipient,
                                                ...(isNewSharingMode ? { contactType } : {}),
                                            };
                                            _recipient.contact = e.target.value;
                                            const _shareRecipients = [...shareRecipients];
                                            _shareRecipients[index] = _recipient;
                                            dispatch(setterShareable({ shareRecipients: _shareRecipients }));
                                        }}
                                    />
                                </Form.Item>
                                {showRemoveButton && (
                                    <Button
                                        style={{
                                            minWidth: 32,
                                            height: 32,
                                            padding: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            justifySelf: 'center',
                                        }}
                                        type="text"
                                        onClick={() => {
                                            clearShareRecipientsError();
                                            const _shareRecipients = [...shareRecipients];
                                            _shareRecipients.splice(shareRecipients.indexOf(recipient), 1);
                                            dispatch(setterShareable({ shareRecipients: _shareRecipients }));
                                        }}
                                        icon={<CloseOutlined fontSize="small" />}
                                        aria-label="Remove recipient"
                                    ></Button>
                                )}
                                {error && (
                                    <div
                                        className="ant-form-item-explain-error"
                                        style={{ gridColumn: isNewSharingMode ? 2 : 1, color: '#ff4d4f' }}
                                    >
                                        {error}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    <Button
                        style={{ marginTop: 10, float: 'right' }}
                        type="text"
                        onClick={() => {
                            clearShareRecipientsError();
                            dispatch(
                                setterShareable({
                                    shareRecipients: [
                                        ...shareRecipients,
                                        isNewSharingMode ? { contactType: 'email' } : {},
                                    ],
                                }),
                            );
                        }}
                        icon={<PlusOutlined />}
                    >
                        Add Recipient
                    </Button>
                    <Checkbox
                        checked={includeAnnotations}
                        style={{ marginTop: 15 }}
                        onChange={(e: CheckboxChangeEvent) => {
                            dispatch(setterShareable({ includeAnnotations: e.target.checked }));
                        }}
                    >
                        Include annotations
                    </Checkbox>
                </div>
            ),
            // <Form.List name="invitations" initialValue={[{
            //     test: 'est',
            // }]}>
            //     {(invitations) => (
            //         <div>
            //             {invitations.toString() + 'asd '}
            //         {invitations.map((field) => (
            //             <Form.Item>
            //                 <Input
            //                     id="contact"
            //                     placeholder="Send to email or phone number"
            //                     // value={null}
            //                     onChange={(e: any) => {
            //                         console.log(e);
            //                         // setInvitationsSelected(e.target.value)
            //                     }}
            //                 />
            //             </Form.Item>
            //         ))}
            //         </div>
            //     )}
            // </Form.List>,
        },
    ];

    return (
        <Tabs
            defaultActiveKey="members"
            items={items}
            onChange={(tabIndex: string) => {
                setCurrentTab(tabIndex as SharedTabType);
            }}
        />
    );
};
